import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { requestSms, RequestSmsResponse } from '../../api/authApi';
import { COUNTRIES, PL_FLAG_SVG, getCountryName } from './countries';

interface PhoneFormProps {
  onSuccess: (phone: string) => void;
  onEmailClick: () => void;
  onRegisterClick: () => void;
  checkoutMode?: boolean;
}



export const PhoneForm: React.FC<PhoneFormProps> = ({ onSuccess, onEmailClick, onRegisterClick, checkoutMode = false }) => {
  const { t, lang } = useI18n();
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [noAccountYet, setNoAccountYet] = useState(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES.find(c => c.code === 'PL') || COUNTRIES[0]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validatePhone = (p: string) => {
    const cleaned = p.replace(/\D/g, '');
    return cleaned.length >= 8;
  };

  const getFullPhone = () => {
    const cleanPhoneStr = phone.replace(/\D/g, '');
    const dialDigits = selectedCountry.dialCode.replace(/\D/g, '');
    let finalPhone = cleanPhoneStr;
    
    if (finalPhone.startsWith(dialDigits) && finalPhone.length > 8 + dialDigits.length) {
       finalPhone = finalPhone.substring(dialDigits.length);
    }
    
    return `${selectedCountry.dialCode}${finalPhone}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    if (!phone) {
      setError(t('err_phone_empty'));
      return;
    }
    if (!validatePhone(phone)) {
      setError(t('err_phone_format'));
      return;
    }

    setIsLoading(true);
    setError('');
    setNoAccountYet(false);

    try {
      const fullPhone = getFullPhone();
      const context = checkoutMode ? 'checkout_returning' : 'login';
      
      const result = await requestSms(fullPhone, context);

      if (result && 'no_account_yet' in result && result.no_account_yet) {
        setNoAccountYet(true);
        return;
      }

      onSuccess(fullPhone);
    } catch (err: any) {
      if (!err.response) {
        setError(t('err_network'));
      } else {
        setError(err.response?.data?.message || t('err_sms_failed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="cab-auth-form">
      <div className="cab-auth-header-wrapper">
        <span className="cab-auth-kicker">{t('login_hi')}</span>
        <h2>{t('login_title2')}</h2>
        <p className="cab-sub">{t('login_choose')}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="cab-field" ref={containerRef} style={{ zIndex: isDropdownOpen ? 100 : 1 }}>
          <label>{t('phone_label')}</label>
          <div className={`cab-phone-input-wrapper ${error ? 'has-error' : ''} ${isDropdownOpen ? 'dropdown-open' : ''}`}>
            <div 
              className="cab-phone-prefix" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDropdownOpen(!isDropdownOpen);
              }}
            >
              {selectedCountry.flagNode}
              <span>{selectedCountry.dialCode}</span>
              <svg 
                width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', pointerEvents: 'none' }}
              >
                <path d="M1 1L5 5L9 1" stroke="#361777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <input 
              type="tel" 
              className="cab-phone-input" 
              placeholder={t('phone_placeholder')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {isDropdownOpen && (
            <div className="cab-country-dropdown">
              {COUNTRIES.map((c) => (
                <div 
                  key={c.code} 
                  className={`cab-country-option ${c.code === selectedCountry.code ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedCountry(c);
                    setIsDropdownOpen(false);
                  }}
                >
                  <div className="cab-country-flag-wrap">{c.flagNode}</div>
                  <span className="cab-country-name">{getCountryName(c.code, lang, c.name)}</span>
                  <span className="cab-country-dial">{c.dialCode}</span>
                </div>
              ))}
            </div>
          )}
        </div>


        {error && (
          <div className="cab-error-msg">
            {error}
            <div style={{ marginTop: '8px', textAlign: 'center' }}>
              <span 
                onClick={() => {
                  if (validatePhone(phone)) {
                    setError('');
                    onSuccess(getFullPhone());
                  } else {
                    setError(t('err_phone_to_enter_code'));
                  }
                }} 
                style={{ textDecoration: 'underline', cursor: 'pointer', color: '#361777', fontWeight: 600 }}
              >
                {t('btn_have_code')}
              </span>
            </div>
          </div>
        )}

        {noAccountYet && (
          <div className="cab-no-account-notice">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{t('no_account_yet_msg')}</span>
          </div>
        )}

        {!noAccountYet && (
          <button 
            type="submit" 
            className="cab-btn-primary" 
            disabled={isLoading}
          >
            {isLoading ? '...' : t('send_sms_btn')}
          </button>
        )}
      </form>

      <p className="cab-no-pw-hint">
        {t('login_no_password_hint')}
      </p>

      <button 
        className="cab-btn-secondary" 
        onClick={onEmailClick}
        type="button"
      >
        {t('login_with_email')}
      </button>

      <div className="cab-auth-foot">
        <h3>{t('no_account')}</h3>
        <button 
          className="cab-btn-outline" 
          onClick={onRegisterClick}
          type="button"
        >
          {t('create_account')}
        </button>
      </div>

      <style>{`
        .cab-auth-form * {
          box-sizing: border-box;
        }
        .cab-auth-form {
          background: #F8F6FC;
          border-radius: 32px;
          padding: 48px 32px;
          width: 100%;
          max-width: 644px;
          margin: 0 auto;
          text-align: center;
          font-family: inherit;
          margin-top: 50px;
        }
        .cab-auth-header-wrapper {
          margin-bottom: 24px;
        }
        .cab-auth-kicker {
          display: block;
          font-size: 26px;
          font-weight: 800;
          color: #361777;
          margin-bottom: 12px;
        }
        .cab-auth-form h2 {
          font-size: 18px;
          font-weight: 600;
          color: #3B2A63;
          margin-bottom: 8px;
        }
        .cab-sub {
          font-size: 15px;
          color: #5A4E7A;
          line-height: 1.4;
          font-weight: 500;
          margin: 0 auto;
          max-width: 280px;
        }
        .cab-field {
          text-align: left;
          margin-bottom: 16px;
          position: relative;
        }
        .cab-field label {
          display: block;
          font-size: 16px;
          font-weight: 700;
          color: #4C3C73;
          margin-bottom: 12px;
        }
        .cab-phone-input-wrapper {
          display: flex;
          align-items: center;
          background: #FFFFFF;
          border-radius: 100px;
          padding: 4px;
          height: 56px;
          box-shadow: 0 4px 16px rgba(45, 21, 97, 0.03);
          border: 1px solid transparent;
          transition: border-color 0.2s;
        }
        .cab-phone-input-wrapper:focus-within, .cab-phone-input-wrapper.dropdown-open {
          border-color: #361777;
        }
        .cab-phone-input-wrapper.has-error {
          border-color: #FF4D4F;
        }
        .cab-phone-prefix {
          display: flex;
          align-items: center;
          background: #EBE4F7;
          color: #361777;
          font-weight: 600;
          font-size: 15px;
          padding: 0 14px;
          border-radius: 100px;
          height: 48px;
          gap: 8px;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s, transform 0.1s;
          position: relative;
          z-index: 2;
        }
        .cab-phone-prefix:hover {
          background: #E0D6F2;
        }
        .cab-phone-prefix:active {
          transform: scale(0.98);
        }
        .cab-flag-icon {
          border-radius: 50%;
          border: 1px solid rgba(0,0,0,0.05);
          display: block;
          pointer-events: none;
        }
        .cab-flag-emoji {
          font-size: 18px;
          line-height: 1;
          display: inline-block;
          width: 20px;
          text-align: center;
          pointer-events: none;
        }
        .cab-phone-input {
          flex: 1;
          border: none;
          background: transparent;
          padding: 0 16px;
          font-size: 16px;
          color: #361777;
          height: 100%;
          font-weight: 500;
          z-index: 1;
        }
        .cab-phone-input:focus {
          outline: none;
        }
        .cab-phone-input::placeholder {
          color: #A49BB6;
          font-weight: 400;
        }
        
        /* Dropdown styles */
        .cab-country-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          width: 100%;
          background: #FFFFFF;
          border-radius: 20px;
          box-shadow: 0 8px 32px rgba(45, 21, 97, 0.08);
          border: 1px solid #EBE4F7;
          z-index: 1000;
          max-height: 260px;
          overflow-y: auto;
          padding: 12px 0;
          animation: cab-dropdown-fade 0.2s ease;
        }
        @keyframes cab-dropdown-fade {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .cab-country-option {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          cursor: pointer;
          transition: background 0.2s;
          gap: 12px;
        }
        .cab-country-option:hover {
          background: #F8F6FC;
        }
        .cab-country-option.selected {
          background: #EBE4F7;
        }
        .cab-country-flag-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
        }
        .cab-country-name {
          flex: 1;
          font-size: 15px;
          color: #361777;
          font-weight: 600;
        }
        .cab-country-dial {
          color: #A49BB6;
          font-size: 15px;
          font-weight: 600;
        }

        .cab-btn-primary {
          width: 100%;
          height: 56px;
          border-radius: 100px;
          background: #361777;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: opacity 0.2s;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: inherit;
        }
        .cab-btn-primary:hover {
          opacity: 0.9;
        }
        .cab-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .cab-no-pw-hint {
          font-size: 13.5px;
          color: #9B8EB8;
          margin-bottom: 16px;
          font-weight: 500;
          line-height: 1.5;
        }
        .cab-btn-secondary {
          width: 100%;
          height: 56px;
          margin-top: 32px;
          border-radius: 100px;
          background: #E6D6FA;
          color: #361777;
          font-size: 16px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: background 0.2s;
        }
        .cab-btn-secondary:hover {
          background: #E0D6F2;
        }
        .cab-auth-foot {
          margin-top: 20px;
          border-top: 1px solid #EBE4F7;
          padding-top: 24px;
        }
        .cab-auth-foot h3 {
          font-size: 15px;
          font-weight: 500;
          color: #5A4E7A;
          margin-bottom: 16px;
        }
        .cab-btn-outline {
          width: 100%;
          height: 56px;
          border-radius: 100px;
          background: transparent;
          color: #361777;
          font-size: 16px;
          font-weight: 600;
          border: 1px solid #361777;
          cursor: pointer;
          transition: background 0.2s;
        }
        .cab-btn-outline:hover {
          background: rgba(45, 21, 97, 0.04);
        }
        .cab-error-msg {
          color: #FF4D4F;
          font-size: 14px;
          margin-bottom: 16px;
          font-weight: 500;
          text-align: left;
        }
        .cab-no-account-notice {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          background: #FFF7ED;
          border: 1px solid #FED7AA;
          color: #92400E;
          border-radius: 12px;
          padding: 14px 16px;
          font-size: 14px;
          line-height: 1.5;
          font-weight: 500;
          margin-bottom: 16px;
          text-align: left;
        }
        .cab-no-account-notice svg {
          flex-shrink: 0;
          margin-top: 1px;
        }
        
        @media (max-width: 480px) {
          .cab-auth-form {
            padding: 32px 20px;
            border-radius: 24px;
          }
          .cab-auth-kicker {
            font-size: 24px;
            margin-bottom: 0px;
          }

           .cab-auth-form h2 {
              font-size: 16px;
            }
        }
      `}</style>
    </div>
  );
};

